// AddNewBed.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Row, Col, Card, Divider, Input, Button, Tag,
  Space, Table, InputNumber, Select, Form, message, Spin,
  Modal, Grid, Steps, Typography, Checkbox, Tooltip, Switch
} from "antd";
import {
  PlusOutlined, UploadOutlined, ArrowLeftOutlined, ArrowRightOutlined, CheckCircleOutlined, CloseCircleFilled
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// NEW: live preview component
import BedLivePreview from "../components/BedLivePreview";

message.config({ top: 80, duration: 2 });
const { Option } = Select;
const { useBreakpoint } = Grid;
const { Title, Text } = Typography;

const MAX_IMAGES = 8;

// ✅ Draft constants
const AUTOSAVE_DELAY = 1500;

const safeParseJSON = (val) => {
  try {
    if (!val) return null;
    return typeof val === "string" ? JSON.parse(val) : val;
  } catch {
    return null;
  }
};

const AddNewBed = () => {
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const userRole = (localStorage.getItem("userRole") || localStorage.getItem("role") || "").trim();
  const isSuperAdmin = /super\s*admin/i.test(userRole);

  // ─────────────────────────── STATE ───────────────────────────
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  // images: antd-like fileList objects
  const [images, setImages] = useState([]);

  const [types, setTypes] = useState(["Non Ottoman", "Ottoman", "Cloud Storage"]);
  const [sizes, setSizes] = useState([]);
  const [priceMap, setPriceMap] = useState({});
  const [costPriceMap, setCostPriceMap] = useState({});
  const [priceBase, setPriceBase] = useState(0);
  const [factoryBase, setFactoryBase] = useState(0);

  // ⬅️ user-editable gap between sizes
  const [priceStep, setPriceStep] = useState(50);

  const [saving, setSaving] = useState(false);

  const [fabrics, setFabrics] = useState([]);
  const [fullFabricColors, setFullFabricColors] = useState([]);
  const [fabricColors, setFabricColors] = useState([]);
  const [selectedFabrics, setSelectedFabrics] = useState([]);
  const [selectedFabricColors, setSelectedFabricColors] = useState([]);
  const [loadingFabrics, setLoadingFabrics] = useState(false);
  const [loadingFabricColors, setLoadingFabricColors] = useState(false);

  // Category: fixed to Beds (hidden)
  const category_name = "Beds";
  const [categoryId, setCategoryId] = useState(null);

  const [progressVisible, setProgressVisible] = useState(false);
  const [currentStepText, setCurrentStepText] = useState("");
  const [operationComplete, setOperationComplete] = useState(false);

  // default 0 (0 = only original, no copies)
  const [duplicateCount, setDuplicateCount] = useState(0);

  const [sizeDimensions, setSizeDimensions] = useState({}); // { [sizeId]: { length, width, height } }

  // NEW: per-size stock map
  const [sizeStocks, setSizeStocks] = useState({}); // { [sizeId]: number }

  const [pkgNonOttoman, setPkgNonOttoman] = useState(4);
  const [pkgOttoman, setPkgOttoman] = useState(5);
  const [pkgCloud, setPkgCloud] = useState(5);
  const [isMerakiDivanOttoman, setIsMerakiDivanOttoman] = useState(false);

  const [uiStep, setUiStep] = useState(0);
  const [form] = Form.useForm();

  // hidden <input> from antd Upload (we trigger it from "+" tile)
  const uploadRef = useRef(null);

  // ─────────────────────────── NEW: Storage cost state ───────────────────────────
  const [cloudCostStorage, setCloudCostStorage] = useState(0); // EUR
  const [ottomanCostsStorage, setOttomanCostsStorage] = useState({}); // { [sizeId]: EUR }

  // NEW: Add to Inventory toggle (same behavior as AddProductForm)
  const [markInventoryPart, setMarkInventoryPart] = useState(false);

  // ─────────────────────────── ✅ DRAFT SYSTEM STATE ───────────────────────────
  const [draftId, setDraftId] = useState(null);
  const [draftSaving, setDraftSaving] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);

  const [autoSaveEnabled, setAutoSaveEnabled] = useState(false);
  const [showDraftChoiceModal, setShowDraftChoiceModal] = useState(false);
  const [latestDraft, setLatestDraft] = useState(null);
  const [checkingLatestDraft, setCheckingLatestDraft] = useState(true);

  // refs
  const saveTimerRef = useRef(null);
  const lastSavedSnapshotRef = useRef(null);
  const isMountedRef = useRef(false);
  const saveInProgressRef = useRef(false);
  const savePendingRef = useRef(null);

  const restoringDraftRef = useRef(false);
  const hasLoadedDraftRef = useRef(false);

  // use your unique column for idempotency
  const draftUuidRef = useRef(`bed-draft-${Date.now()}-${Math.floor(Math.random() * 100000)}`);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  // ─────────────────────────── HELPERS ───────────────────────────
  const fileToUrl = (file) => {
    if (!file) return "";
    return file.url || file.thumbUrl || (file.originFileObj ? URL.createObjectURL(file.originFileObj) : "");
  };

  const getImagesMeta = () =>
    (images || []).map((f) => ({
      uid: f.uid,
      name: f.name,
      url: f.url || null,
      thumbUrl: f.thumbUrl || null,
    }));

  const setMainIndex = (index) => {
    if (index <= 0) return;
    setImages((list) => {
      const copy = [...list];
      const [picked] = copy.splice(index, 1);
      copy.unshift(picked);
      return copy;
    });
    scheduleDraftSave();
  };

  const removeAtIndex = (index) => {
    setImages((list) => list.filter((_, i) => i !== index));
    scheduleDraftSave();
  };

  // ─────────────────────────── ✅ DRAFT HELPERS ───────────────────────────
  const buildDraftSnapshot = () => {
    return {
      draftType: "bed",
      draftUuid: draftUuidRef.current,

      uiStep,
      title,
      description,

      imagesMeta: getImagesMeta(),

      types,
      sizes,
      priceMap,
      costPriceMap,
      priceBase,
      factoryBase,
      priceStep,

      fabrics,
      fullFabricColors,
      fabricColors,
      selectedFabrics,
      selectedFabricColors,

      pkgNonOttoman,
      pkgOttoman,
      pkgCloud,
      isMerakiDivanOttoman,

      duplicateCount,
      sizeDimensions,
      sizeStocks,

      cloudCostStorage,
      ottomanCostsStorage,

      categoryId,
      category_name,

      markInventoryPart,

      savedAt: new Date().toISOString(),
    };
  };

  const scheduleDraftSave = (delay = AUTOSAVE_DELAY) => {
    if (!autoSaveEnabled) return;
    if (restoringDraftRef.current) return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    const snapshot = buildDraftSnapshot();
    const snapshotStr = JSON.stringify(snapshot);
    if (lastSavedSnapshotRef.current && lastSavedSnapshotRef.current === snapshotStr) return;

    saveTimerRef.current = setTimeout(() => {
      performSave(snapshot);
    }, delay);
  };

  const performSave = async (snapshotObj = null) => {
    if (!isMountedRef.current) return;
    if (!autoSaveEnabled) return;
    if (restoringDraftRef.current) return;

    const snapshot = snapshotObj || buildDraftSnapshot();
    const snapshotStr = JSON.stringify(snapshot);

    if (lastSavedSnapshotRef.current && lastSavedSnapshotRef.current === snapshotStr) return;

    if (saveInProgressRef.current) {
      savePendingRef.current = snapshot;
      return;
    }

    setDraftSaving(true);
    saveInProgressRef.current = true;

    try {
      const payload = {
        title: (title || "").trim() || "Untitled bed draft",
        data: snapshot,
        images: snapshot.imagesMeta || [],
        currentStep: Number.isFinite(uiStep) ? uiStep : 0,
        mainImageIndex: 0,

        draftType: "bed",
        draftUuid: draftUuidRef.current,

        // backward-compatible keys
        clientId: draftUuidRef.current,
        clientDraftId: draftUuidRef.current,
      };

      if (draftId) {
        const resp = await axiosInstance.put(`/admin/drafts/${draftId}`, payload);
        if (resp?.data?.success) {
          lastSavedSnapshotRef.current = snapshotStr;
          setDraftSavedAt(new Date());
          message.success("Draft updated");
        } else {
          throw new Error(resp?.data?.message || "Failed to update draft");
        }
      } else {
        const resp = await axiosInstance.post("/admin/drafts", payload);
        if (resp?.data?.success) {
          const newId =
            resp.data.draftId ||
            resp.data.id ||
            resp.data._id ||
            resp.data.data?.id ||
            resp.data.draft?.id;

          if (newId) {
            setDraftId(newId);

            // optional URL
            try {
              const url = new URL(window.location.href);
              url.searchParams.set("draftId", String(newId));
              window.history.replaceState({}, "", url.toString());
            } catch {
              // ignore
            }
          }

          lastSavedSnapshotRef.current = snapshotStr;
          setDraftSavedAt(new Date());
          message.success("Draft saved");
        } else {
          throw new Error(resp?.data?.message || "Failed to create draft");
        }
      }
    } catch (e) {
      console.error("Bed draft save error:", e);
      message.error("Draft save failed");
    } finally {
      saveInProgressRef.current = false;
      if (isMountedRef.current) setDraftSaving(false);

      if (savePendingRef.current) {
        const pending = savePendingRef.current;
        savePendingRef.current = null;
        if (JSON.stringify(pending) !== lastSavedSnapshotRef.current) {
          performSave(pending);
        }
      }
    }
  };

  const handleManualSaveDraft = async () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    await performSave();
  };

  const loadDraftIntoForm = async (draftIdToLoad) => {
    try {
      restoringDraftRef.current = true;

      const resp = await axiosInstance.get(`/admin/drafts/${draftIdToLoad}`);
      if (!resp.data.success) {
        message.error("Failed to load draft");
        return;
      }

      const draft = resp.data.draft;
      const doc = safeParseJSON(draft.data);

      if (!doc || doc.draftType !== "bed") {
        message.error("This draft is not a bed draft");
        return;
      }

      hasLoadedDraftRef.current = true;

      // if stored, reuse same uuid
      if (doc.draftUuid) draftUuidRef.current = doc.draftUuid;

      // restore fields
      setUiStep(Number.isFinite(doc.uiStep) ? doc.uiStep : 0);
      setTitle(doc.title || "");
      setDescription(doc.description || "");

      setTypes(Array.isArray(doc.types) && doc.types.length ? doc.types : ["Non Ottoman", "Ottoman", "Cloud Storage"]);
      setSizes(Array.isArray(doc.sizes) ? doc.sizes : []);
      setPriceMap(doc.priceMap || {});
      setCostPriceMap(doc.costPriceMap || {});
      setPriceBase(Number(doc.priceBase || 0));
      setFactoryBase(Number(doc.factoryBase || 0));
      setPriceStep(Number(doc.priceStep || 50));

      setFabrics(Array.isArray(doc.fabrics) ? doc.fabrics : fabrics);
      setFullFabricColors(Array.isArray(doc.fullFabricColors) ? doc.fullFabricColors : fullFabricColors);
      setFabricColors(Array.isArray(doc.fabricColors) ? doc.fabricColors : fabricColors);

      setSelectedFabrics(Array.isArray(doc.selectedFabrics) ? doc.selectedFabrics : []);
      setSelectedFabricColors(Array.isArray(doc.selectedFabricColors) ? doc.selectedFabricColors : []);

      setPkgNonOttoman(Number(doc.pkgNonOttoman || 4));
      setPkgOttoman(Number(doc.pkgOttoman || 5));
      setPkgCloud(Number(doc.pkgCloud || 5));
      setIsMerakiDivanOttoman(!!doc.isMerakiDivanOttoman);

      setDuplicateCount(Number(doc.duplicateCount || 0));
      setSizeDimensions(doc.sizeDimensions || {});
      setSizeStocks(doc.sizeStocks || {});

      setCloudCostStorage(Number(doc.cloudCostStorage || 0));
      setOttomanCostsStorage(doc.ottomanCostsStorage || {});

      if (doc.categoryId != null) setCategoryId(doc.categoryId);
      setMarkInventoryPart(!!doc.markInventoryPart);

      // restore images meta (NOTE: local files can't be restored, only urls)
      const meta = Array.isArray(doc.imagesMeta) ? doc.imagesMeta : [];
      setImages(
        meta.map((m) => ({
          uid: m.uid || `${Date.now()}-${Math.random()}`,
          name: m.name || "file",
          url: m.url || null,
          thumbUrl: m.thumbUrl || m.url || null,
          status: "done",
        }))
      );

      setDraftId(draftIdToLoad);

      lastSavedSnapshotRef.current = JSON.stringify(doc);
      setDraftSavedAt(draft.updatedAt ? new Date(draft.updatedAt) : new Date());

      setShowDraftChoiceModal(false);
      message.success("Draft loaded");
    } catch (err) {
      console.error("Load draft error:", err);
      message.error("Error loading draft");
    } finally {
      setTimeout(() => {
        restoringDraftRef.current = false;
      }, 0);
    }
  };

  // ✅ check latest BED draft on load OR if URL has draftId
  useEffect(() => {
    const draftIdFromUrl = queryParams.get("draftId");

    const checkLatestDraft = async () => {
      try {
        if (draftIdFromUrl) {
          setAutoSaveEnabled(true);
          await loadDraftIntoForm(draftIdFromUrl);
          return;
        }

        const res = await axiosInstance.get("/admin/drafts/latest?type=bed");

        if (res.data.success && res.data.draft) {
          const d = res.data.draft;
          const doc = safeParseJSON(d.data);

          if (doc && doc.draftType === "bed") {
            setLatestDraft(d);
            setShowDraftChoiceModal(true);
            return;
          }
        }

        // No bed draft
        setAutoSaveEnabled(true);
      } catch (err) {
        console.error("Failed to check latest bed draft:", err);
        setAutoSaveEnabled(true);
      } finally {
        setCheckingLatestDraft(false);
      }
    };

    checkLatestDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─────────────────────────── FETCHES ───────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.get("/category/categories");
        if (data.success && Array.isArray(data.categories)) {
          const bedsCat = data.categories.find(c => c.category_name === category_name);
          if (bedsCat) setCategoryId(bedsCat.id);
          else message.error(`Category "${category_name}" not found`);
        }
      } catch {
        message.error("Error fetching categories");
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.get("/product/sizes");
        const filteredSizes = (data.sizes || []).filter(size =>
          size.size && size.size.trim().toLowerCase() !== "queen"
        );

        // ✅ don't overwrite if draft already loaded
        if (!hasLoadedDraftRef.current) {
          setSizes(filteredSizes);

          const initialPriceMap = {};
          const initialCostMap = {};
          const initialDims = {};
          const initialStocks = {};
          filteredSizes.forEach((s) => {
            initialPriceMap[s.id] = 0;
            initialCostMap[s.id] = 0;
            initialDims[s.id] = { length: "", width: "", height: "" };
            initialStocks[s.id] = 0;
          });
          setPriceMap(initialPriceMap);
          setCostPriceMap(initialCostMap);
          setSizeDimensions(initialDims);
          setSizeStocks(initialStocks);
        } else {
          // if draft loaded and sizes empty, fill from api
          setSizes((prev) => (prev && prev.length ? prev : filteredSizes));
        }
      } catch {
        message.error("Failed to load sizes");
      }
    })();
  }, []);

  // NEW: fetch storage costs (cloud + ottoman per size) once we know categoryId
  useEffect(() => {
    if (!categoryId) return;
    (async () => {
      try {
        const [cloudRes, pricesRes] = await Promise.all([
          axiosInstance.get(`/admin/storage-prices/cloud?categoryId=${categoryId}`),
          axiosInstance.get(`/admin/storage-prices?categoryId=${categoryId}`),
        ]);
        setCloudCostStorage(cloudRes.data?.cloudCost ?? 0);
        setOttomanCostsStorage(pricesRes.data?.ottomanCosts || {});
      } catch {
        // silent fail
      }
    })();
  }, [categoryId]);

  const rebuildPriceMaps = () => {
    const newPriceMap = {};
    const newCostMap = {};
    sizes.forEach((s, index) => {
      const sizeIncrement = (Number(priceStep) || 0) * index; // ⬅️ use user gap
      newPriceMap[s.id] = priceBase + sizeIncrement;
      newCostMap[s.id] = factoryBase + sizeIncrement;
    });
    setPriceMap(newPriceMap);
    setCostPriceMap(newCostMap);
    scheduleDraftSave();
  };

  useEffect(() => {
    if (restoringDraftRef.current) return;
    if (sizes.length > 0 && types.length > 0) rebuildPriceMaps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceBase, factoryBase, priceStep, sizes, types]);

  useEffect(() => {
    (async () => {
      setLoadingFabrics(true);
      try {
        const { data } = await axiosInstance.get("/admin/fabrics-with-colors");
        const fetched = data.fabrics || [];
        setFabrics(fetched);

        const allCols = fetched.flatMap(f =>
          (f.fabricColors || []).map(c => ({ ...c, fabricId: f.id }))
        );
        setFullFabricColors(allCols);

        // ✅ don't overwrite selections if draft loaded
        if (!hasLoadedDraftRef.current) {
          const allFids = fetched.map(f => f.id);
          setSelectedFabrics(allFids);
          filterFabricColors(allFids, allCols);
        }
      } catch {
        message.error("Failed to load fabrics/colors");
      } finally {
        setLoadingFabrics(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterFabricColors = (fids, source = fullFabricColors) => {
    setLoadingFabricColors(true);
    const filtered = (source || []).filter(c => fids.includes(c.fabricId));
    setFabricColors(filtered);
    setSelectedFabricColors(filtered.map(c => c.id));
    setLoadingFabricColors(false);
    scheduleDraftSave();
  };

  // ─────────────────────────── HANDLERS ───────────────────────────
  const handleAddVariant = () => {
    const newSize = prompt("Enter new size name:");
    if (!newSize) return;
    if (sizes.some(s => s.size === newSize)) {
      message.warning("That size already exists");
      return;
    }
    const tempId = Math.min(0, ...sizes.map(s => s.id)) - 1;
    setSizes(prev => [...prev, { id: tempId, size: newSize, multiplier: sizes.length + 1 }]);

    setPriceMap(prev => ({ ...prev, [tempId]: 0 }));
    setCostPriceMap(prev => ({ ...prev, [tempId]: 0 }));
    setSizeDimensions(prev => ({ ...prev, [tempId]: { length: "", width: "", height: "" } }));
    setSizeStocks(prev => ({ ...prev, [tempId]: 0 }));
    scheduleDraftSave();
  };

  const handleRemoveVariant = id => {
    setSizes(prev => prev.filter(s => s.id !== id));
    setPriceMap(prev => { const m = { ...prev }; delete m[id]; return m; });
    setCostPriceMap(prev => { const m = { ...prev }; delete m[id]; return m; });
    setSizeDimensions(prev => { const n = { ...prev }; delete n[id]; return n; });
    setSizeStocks(prev => { const n = { ...prev }; delete n[id]; return n; });
    scheduleDraftSave();
  };

  const handleAddType = () => {
    const t = prompt("Enter new type:");
    if (t && !types.includes(t)) {
      setTypes(prev => [...prev, t]);
      rebuildPriceMaps();
      scheduleDraftSave();
    }
  };

  const handleRemoveType = t => {
    setTypes(prev => {
      const next = prev.filter(x => x !== t);
      rebuildPriceMaps();
      return next;
    });
    scheduleDraftSave();
  };

  const handleDuplicateCountChange = (value) => {
    if (value == null) setDuplicateCount(0);
    else setDuplicateCount(Math.max(0, Math.min(20, Math.floor(value))));
    scheduleDraftSave();
  };

  const hasOttoman = types.some(t => /ottoman/i.test(t));
  const hasCloud = types.some(t => /cloud\s*storage/i.test(t));

  // ─────────────────────────── SUBMIT ───────────────────────────
  const handleSubmit = async () => {
    if (!categoryId || !title || !description || images.length === 0 || types.length === 0 || sizes.length === 0) {
      message.error("All fields are required");
      return;
    }

    setSaving(true);
    setProgressVisible(true);
    setCurrentStepText("Creating products...");

    const chosenColorIds = fabricColors
      .filter(c => selectedFabricColors.includes(c.id))
      .map(c => c.id);

    const fabricColorIdsMap = {};
    selectedFabrics.forEach((fid) => {
      const ofThisFabric = fabricColors.filter((c) => c.fabricId === fid);
      fabricColorIdsMap[fid] = ofThisFabric
        .filter((c) => selectedFabricColors.includes(c.id))
        .map((c) => c.id);
    });

    const fullPriceMap = { ...priceMap };

    const computedCostMap = {};
    sizes.forEach((s) => {
      const base = Number(costPriceMap[s.id] || 0);
      computedCostMap[s.id] = Number.isFinite(base) ? base : 0;
    });

    const zeroPriceMap = sizes.reduce((acc, s) => { acc[s.id] = 0; return acc; }, {});
    const zeroCostMap = sizes.reduce((acc, s) => { acc[s.id] = 0; return acc; }, {});

    const sizeDimensionsPayload = {};
    sizes.forEach((s) => {
      const d = sizeDimensions[s.id] || {};
      sizeDimensionsPayload[s.id] = {
        length: (d.length ?? "").toString().trim() || null,
        width: (d.width ?? "").toString().trim() || null,
        height: (d.height ?? "").toString().trim() || null,
      };
    });

    const sizeStocksPayload = {};
    sizes.forEach((s) => {
      sizeStocksPayload[s.id] = Number(sizeStocks[s.id] || 0);
    });
    const totalStock = Object.values(sizeStocksPayload).reduce((sum, v) => sum + (Number(v) || 0), 0);

    const createdIds = [];

    const baseTitle = (title || "").trim().replace(/\s+copy\s+\d+$/i, "");

    for (let i = 0; i <= duplicateCount; i++) {
      const isOriginal = i === 0;
      try {
        const firstSizeId = sizes[0]?.id;
        const finalTitle = isOriginal ? baseTitle : `${baseTitle} copy ${i}`;

        const payload = {
          product_name: finalTitle,
          description: description,
          status: "in stock",
          stock: totalStock > 0 ? totalStock : 1,
          type: types[0],
          types,
          weight: null,
          shippingAvailable: false,
          price_euro: isOriginal ? (priceMap[firstSizeId] || 0) : 0,
          factory_price: isOriginal
            ? (isSuperAdmin ? (computedCostMap[firstSizeId] || 0) : (priceMap[firstSizeId] || 0))
            : 0,
          categoryIds: [categoryId],
          sizeIds: sizes.map(s => s.id),
          fabricIds: selectedFabrics,
          fabricColorIds: chosenColorIds,
          priceMap: isOriginal ? fullPriceMap : zeroPriceMap,
          costPriceMap: isOriginal
            ? (isSuperAdmin ? computedCostMap : fullPriceMap)
            : (isSuperAdmin ? zeroCostMap : zeroPriceMap),
          packageCounts: {
            "Non Ottoman": Number(pkgNonOttoman) || 1,
            "Ottoman": Number(pkgOttoman) || 1,
            "Cloud Storage": Number(pkgCloud) || 1,
          },
          isMerakiDivanOttoman: !!isMerakiDivanOttoman,
          sizeDimensions: sizeDimensionsPayload,
          allowEmptyTitle: !isOriginal,
          isInventoryPart: !!markInventoryPart,
          sizeStocks: sizeStocksPayload,
        };

        setCurrentStepText(
          `Creating ${isOriginal ? "original" : "duplicate"} product ${isOriginal ? 1 : i} of ${1 + duplicateCount}...`
        );

        const resp = await axiosInstance.post("/admin/add-product", payload);

        if (resp.data.success) {
          const productId = resp.data.productId;
          createdIds.push(productId);

          if (isOriginal && images.length > 0) {
            setCurrentStepText(`Uploading images for product ${isOriginal ? 1 : i}...`);
            const fd = new FormData();
            fd.append("productId", productId);
            images.forEach(img => {
              if (img.originFileObj) fd.append("images", img.originFileObj);
            });
            await axiosInstance.post("/admin/product/images", fd, {
              headers: { "Content-Type": "multipart/form-data" }
            });
          }

          setCurrentStepText(`Creating variations for product ${isOriginal ? 1 : i}...`);
          const blueprint = {
            productId,
            sizeIds: sizes.map((s) => s.id),
            fabricIds: selectedFabrics,
            fabricColorIds: fabricColorIdsMap,
            types,
            priceMap: isOriginal ? fullPriceMap : zeroPriceMap,
            costPriceMap: isOriginal
              ? (isSuperAdmin ? computedCostMap : fullPriceMap)
              : (isSuperAdmin ? zeroCostMap : zeroPriceMap),
          };

          await axiosInstance.post("/admin/product-variation", blueprint, {
            headers: { "Content-Type": "application/json" },
          });
        } else {
          throw new Error("Failed to create product");
        }
      } catch {
        message.error(`Error creating ${i === 0 ? "original" : `duplicate ${i}`} product`);
        continue;
      }
    }

    // ✅ delete draft after publish (best effort)
    if (draftId) {
      try { await axiosInstance.delete(`/admin/drafts/${draftId}`); } catch {}
    }

    setOperationComplete(true);
    message.success(`Successfully created ${createdIds.length} bed(s)!`);
    setTimeout(() => navigate("/products"), 1500);
  };

  // ─────────────────────────── TABLE ───────────────────────────
  const columns = [
    { title: "Variant", dataIndex: "size", key: "size" },

    ...(isSuperAdmin
      ? [
          {
            title: "Cost Price(£)",
            dataIndex: "id",
            key: "factory_price",
            render: (id) => (
              <InputNumber
                min={0}
                value={costPriceMap[id]}
                onChange={(v) => { setCostPriceMap((prev) => ({ ...prev, [id]: v })); scheduleDraftSave(); }}
                style={{ width: screens.xs ? 80 : 100 }}
                size={screens.xs ? "small" : "middle"}
              />
            ),
          },
        ]
      : []),

    {
      title: "Sale Price(€)",
      dataIndex: "id",
      key: "price",
      render: (id) => (
        <InputNumber
          min={0}
          value={priceMap[id]}
          onChange={(v) => { setPriceMap((prev) => ({ ...prev, [id]: v })); scheduleDraftSave(); }}
          style={{ width: screens.xs ? 80 : 100 }}
          size={screens.xs ? "small" : "middle"}
        />
      ),
    },

    {
      title: "Stock",
      dataIndex: "id",
      key: "stock",
      render: (id) => (
        <InputNumber
          min={0}
          max={99999}
          value={sizeStocks[id] ?? 0}
          onChange={(v) => { setSizeStocks((prev) => ({ ...prev, [id]: Number(v || 0) })); scheduleDraftSave(); }}
          style={{ width: screens.xs ? 80 : 100 }}
          size={screens.xs ? "small" : "middle"}
        />
      ),
    },

    {
      title: "Length",
      dataIndex: "id",
      key: "length",
      render: (id) => (
        <Input
          value={sizeDimensions[id]?.length ?? ""}
          onChange={(e) => {
            setSizeDimensions((prev) => ({
              ...prev,
              [id]: { ...(prev[id] || {}), length: e.target.value },
            }));
            scheduleDraftSave();
          }}
          placeholder="e.g., 200 cm"
          size={screens.xs ? "small" : "middle"}
        />
      ),
    },
    {
      title: "Width",
      dataIndex: "id",
      key: "width",
      render: (id) => (
        <Input
          value={sizeDimensions[id]?.width ?? ""}
          onChange={(e) => {
            setSizeDimensions((prev) => ({
              ...prev,
              [id]: { ...(prev[id] || {}), width: e.target.value },
            }));
            scheduleDraftSave();
          }}
          placeholder="e.g., 150 cm"
          size={screens.xs ? "small" : "middle"}
        />
      ),
    },
    {
      title: "Height",
      dataIndex: "id",
      key: "height",
      render: (id) => (
        <Input
          value={sizeDimensions[id]?.height ?? ""}
          onChange={(e) => {
            setSizeDimensions((prev) => ({
              ...prev,
              [id]: { ...(prev[id] || {}), height: e.target.value },
            }));
            scheduleDraftSave();
          }}
          placeholder="e.g., 30 cm"
          size={screens.xs ? "small" : "middle"}
        />
      ),
    },
  ];

  const dataSource = sizes.map(s => ({ key: s.id, id: s.id, size: s.size }));

  const RequiredLabel = ({ text }) => (
    <span>
      {text}
      <span style={{ color: "red", marginLeft: 4 }}>*</span>
    </span>
  );

  const steps = [
    { title: "Product Details", icon: <UploadOutlined /> },
    { title: "Types & Packaging", icon: <PlusOutlined /> },
    { title: "Fabrics & Colors", icon: <PlusOutlined /> },
    { title: "Variants & Pricing", icon: <PlusOutlined /> },
  ];
  const responsiveSteps = steps.map((s) => ({
    ...s,
    title: window.innerWidth < 768 ? "" : s.title,
  }));

  const next = () => { setUiStep((s) => Math.min(s + 1, steps.length - 1)); setTimeout(scheduleDraftSave, 0); };
  const prev = () => { setUiStep((s) => Math.max(s - 1, 0)); setTimeout(scheduleDraftSave, 0); };

  // ─────────────────────────── RENDER ───────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <Row gutter={[24, 24]} align="start">
        {/* LEFT: main form card */}
        <Col xs={24} lg={16}>
          <Card
            className="max-w-5xl w-full mx-auto bg-white rounded-2xl shadow-2xl border border-white/20"
            bodyStyle={{ padding: "32px 24px" }}
          >
            <div className="text-center mb-8">
              <Title level={2} className="!mb-2 !text-gray-800 font-bold">
                Add New Bed
              </Title>
              <Text type="secondary" className="text-gray-600 text-lg">
                Step {uiStep + 1} of {steps.length} • Complete all steps to create your bed listing
              </Text>
            </div>

            <div className="mb-10 px-4">
              <Steps current={uiStep} items={responsiveSteps} size="small" responsive={false} className="custom-steps" />
            </div>

            {/* STEP 0: PRODUCT DETAILS */}
            {uiStep === 0 && (
              <div className="animate-fade-in">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 bg-emerald-100 rounded-full flex items-center justify-center">
                    <PlusOutlined className="text-emerald-600" />
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-gray-800">Product Details</div>
                    <div className="text-gray-500 text-sm">Product name & description on the left, images on the right</div>
                  </div>
                </div>

                <Row gutter={24} align="top">
                  {/* LEFT: Name + Description */}
                  <Col xs={24} md={14}>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <RequiredLabel text="Product Name" />
                      </label>
                      <Input
                        value={title}
                        onChange={(e) => { setTitle(e.target.value); scheduleDraftSave(); }}
                        placeholder="Enter product name"
                        size="large"
                      />
                    </div>

                    <div className="mb-2 font-medium text-gray-700">
                      <RequiredLabel text="Description" />
                    </div>
                    <div
                      style={{
                        height: screens.xs ? 220 : 300,
                        overflowY: "auto",
                        border: "1px solid #e5e7eb",
                        borderRadius: 8
                      }}
                    >
                      <ReactQuill
                        value={description}
                        onChange={(val) => { setDescription(val); scheduleDraftSave(); }}
                        style={{ height: "100%" }}
                        modules={{
                          toolbar: [
                            ["bold", "italic", "underline"],
                            [{ header: 1 }, { header: 2 }],
                            [{ list: "ordered" }, { list: "bullet" }],
                            ["link", "image"],
                            ["clean"]
                          ]
                        }}
                      />
                    </div>
                  </Col>

                  {/* RIGHT: Image Upload Section (UNCHANGED UI) */}
                  <Col xs={24} md={10} className="mt-[-50px]">
                    <Card
                      bodyStyle={{ padding: 16 }}
                      className="bg-slate-50"
                      style={{ borderRadius: 12 }}
                    >
                      <div className="text-sm font-medium text-gray-700 mb-3">
                        <RequiredLabel text="Upload Media" />
                      </div>

                      <div
                        style={{
                          border: "1px dashed #e5e7eb",
                          borderRadius: 12,
                          padding: 12,
                          background: "linear-gradient(180deg,#f8fbff, #eef3ff)",
                        }}
                      >
                        <div
                          style={{
                            height: 240,
                            borderRadius: 12,
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#fff"
                          }}
                        >
                          {images.length ? (
                            <img
                              src={fileToUrl(images[0])}
                              alt="main"
                              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "cover" }}
                            />
                          ) : (
                            <div style={{ color: "#9ca3af" }}>No image selected</div>
                          )}
                        </div>

                        {/* Thumbnails row (UNCHANGED UI) */}
                        <div style={{ display: "flex", gap: 12, marginTop: 12, flexWrap: "wrap" }}>
                          {images.map((f, i) => (
                            <div key={f.uid || i} className="thumb-wrapper">
                              <button
                                type="button"
                                className={`thumb ${i === 0 ? "active" : ""}`}
                                onClick={() => setMainIndex(i)}
                              >
                                <img src={fileToUrl(f)} alt={`thumb-${i}`} />

                                <div className="move-controls">
                                  <button
                                    type="button"
                                    className={`move-btn left ${i === 0 ? "disabled" : ""}`}
                                    disabled={i === 0}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setImages(prev => {
                                        const arr = [...prev];
                                        const [m] = arr.splice(i, 1);
                                        arr.splice(i - 1, 0, m);
                                        return arr;
                                      });
                                      scheduleDraftSave();
                                    }}
                                  >
                                    ‹
                                  </button>

                                  <button
                                    type="button"
                                    className={`move-btn right ${i === images.length - 1 ? "disabled" : ""}`}
                                    disabled={i === images.length - 1}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setImages(prev => {
                                        const arr = [...prev];
                                        const [m] = arr.splice(i, 1);
                                        arr.splice(i + 1, 0, m);
                                        return arr;
                                      });
                                      scheduleDraftSave();
                                    }}
                                  >
                                    ›
                                  </button>
                                </div>

                                <div className="position-badge">{i + 1}</div>
                              </button>

                              <button
                                type="button"
                                className="remove-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeAtIndex(i);
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          ))}

                          {images.length < MAX_IMAGES && (
                            <div
                              onClick={() => uploadRef.current && uploadRef.current.click()}
                              style={{
                                width: 72,
                                height: 72,
                                borderRadius: 10,
                                border: "1px dashed #c7d2fe",
                                background: "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer"
                              }}
                            >
                              <PlusOutlined style={{ color: "#10b981", fontSize: 18 }} />
                            </div>
                          )}
                        </div>

                        <input
                          ref={uploadRef}
                          type="file"
                          accept="image/*"
                          multiple
                          style={{ display: "none" }}
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            if (!files.length) return;
                            setImages((prev) => {
                              const next = [...prev];
                              files.slice(0, MAX_IMAGES - next.length).forEach((file) => {
                                next.push({
                                  uid: `${Date.now()}-${Math.random()}`,
                                  name: file.name,
                                  status: "done",
                                  originFileObj: file,
                                });
                              });
                              return next;
                            });
                            scheduleDraftSave();
                            e.target.value = "";
                          }}
                        />
                      </div>
                    </Card>
                  </Col>
                </Row>
              </div>
            )}

            {/* STEP 1: TYPES & PACKAGING */}
            {uiStep === 1 && (
              <div className="animate-fade-in">
                <div className="mb-6">
                  <RequiredLabel text="Types" />
                  <Space wrap style={{ gap: 8 }}>
                    {types.map(t => (
                      <Tag key={t} closable onClose={() => handleRemoveType(t)} className="text-sm">
                        {t}
                      </Tag>
                    ))}
                    <Button type="dashed" onClick={handleAddType} icon={<PlusOutlined />}>
                      Add Type
                    </Button>
                  </Space>
                </div>

                <Divider orientation="left">Packaging</Divider>
                <Row gutter={16}>
                  <Col xs={24} md={8}>
                    <label className="ant-form-item-required">Non Ottoman packages</label>
                    <InputNumber
                      min={1}
                      value={pkgNonOttoman}
                      onChange={(v) => { setPkgNonOttoman(Number(v) || 1); scheduleDraftSave(); }}
                      style={{ width: "100%" }}
                    />
                  </Col>
                  <Col xs={24} md={8}>
                    <label className="ant-form-item-required">Ottoman packages</label>
                    <InputNumber
                      min={1}
                      value={pkgOttoman}
                      onChange={(v) => { setPkgOttoman(Number(v) || 1); scheduleDraftSave(); }}
                      style={{ width: "100%" }}
                    />
                  </Col>
                  <Col xs={24} md={8}>
                    <label>Cloud Storage packages</label>
                    <InputNumber
                      min={1}
                      value={pkgCloud}
                      onChange={(v) => { setPkgCloud(Number(v) || 1); scheduleDraftSave(); }}
                      style={{ width: "100%" }}
                    />
                  </Col>
                </Row>
              </div>
            )}

            {/* STEP 2: FABRICS & COLORS */}
            {uiStep === 2 && (
              <div className="animate-fade-in">
                <Divider orientation="left">Fabric Options</Divider>
                <Row gutter={16}>
                  <Col span={24}>
                    <RequiredLabel text="Fabrics" />
                    <Spin spinning={loadingFabrics}>
                      <Select
                        mode="multiple"
                        placeholder="Select fabrics"
                        loading={loadingFabrics}
                        value={selectedFabrics}
                        onChange={(vals) => {
                          setSelectedFabrics(vals);
                          filterFabricColors(vals);
                        }}
                        style={{ width: "100%" }}
                        size="large"
                        className="rounded-lg"
                        maxTagCount={screens.xs ? 1 : undefined}
                        dropdownRender={(menu) => {
                          const allSelected = selectedFabrics.length === fabrics.length;
                          return (
                            <div className="bg-white rounded-lg shadow-lg border border-gray-200">
                              <div
                                className="p-3 font-semibold cursor-pointer hover:bg-gray-50 rounded-t-lg flex justify-between items-center border-b border-gray-100"
                                onClick={() => {
                                  const next = allSelected ? [] : fabrics.map((f) => f.id);
                                  setSelectedFabrics(next);
                                  filterFabricColors(next);
                                }}
                              >
                                <span className="text-gray-700">Select All Fabrics</span>
                                <Checkbox checked={allSelected} className="scale-110" />
                              </div>
                              <div className="max-h-64 overflow-y-auto">{menu}</div>
                            </div>
                          );
                        }}
                      >
                        {fabrics.map((f) => {
                          const saleInc = Number(f.fabric_price || 0).toFixed(2);
                          const costInc = Number(f.fabric_cost || 0);
                          return (
                            <Option key={f.id} value={f.id}>
                              <div className="flex items-center justify-between">
                                <span className="text-gray-800">{f.fabric_name}</span>
                                <span className="ml-3 inline-flex items-center gap-2">
                                  <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-700">
                                    {`+€${saleInc}`}
                                  </span>
                                  <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">
                                    {`Cost: £${costInc.toFixed(2)}`}
                                  </span>
                                </span>
                              </div>
                            </Option>
                          );
                        })}
                      </Select>
                    </Spin>
                  </Col>
                </Row>

                <Row style={{ marginTop: 20 }} gutter={[12, 12]}>
                  {selectedFabrics.map((fid) => {
                    const fabric = fabrics.find((f) => f.id === fid);
                    if (!fabric) return null;
                    const colorsOfFabric = (fullFabricColors || []).filter((c) => c.fabricId === fid);
                    const selectedOfFabric = selectedFabricColors.filter((id) =>
                      colorsOfFabric.some((c) => c.id === id)
                    );

                    const saleInc = Number(fabric.fabric_price || 0).toFixed(2);
                    const costInc = Number(fabric.fabric_cost || 0);

                    return (
                      <Col span={24} key={fid}>
                        <div className="bg-white rounded-lg border border-gray-200 p-6">
                          <div className="flex items-center justify-between mb-2">
                            <div className="font-semibold text-gray-800 flex items-center gap-2">
                              <span>{fabric.fabric_name}</span>
                              <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-700">
                                {`+€${saleInc}`}
                              </span>
                              <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">
                                {`Cost: £${costInc.toFixed(2)}`}
                              </span>
                            </div>

                            <Space size={8}>
                              <Button
                                size="small"
                                className="px-3 py-0.5 rounded-full border border-blue-500 text-blue-600 hover:text-white hover:bg-blue-500 hover:border-blue-500 transition"
                                onClick={() => {
                                  const allIds = colorsOfFabric.map((c) => c.id);
                                  const uniq = Array.from(new Set([...(selectedFabricColors || []), ...allIds]));
                                  setSelectedFabricColors(uniq);
                                  scheduleDraftSave();
                                }}
                              >
                                Select All
                              </Button>

                              <Button
                                size="small"
                                className="px-3 py-0.5 rounded-full border border-red-500 text-red-500 hover:text-white hover:bg-red-500 hover:border-red-500 transition"
                                onClick={() => {
                                  const idsOfFabric = new Set(colorsOfFabric.map((c) => c.id));
                                  setSelectedFabricColors((prev) => prev.filter((id) => !idsOfFabric.has(id)));
                                  scheduleDraftSave();
                                }}
                              >
                                Clear
                              </Button>
                            </Space>
                          </div>

                          <Checkbox.Group
                            style={{ width: "100%" }}
                            value={selectedOfFabric}
                            onChange={(vals) => {
                              const ofThis = new Set(colorsOfFabric.map((c) => c.id));
                              setSelectedFabricColors((prev) => {
                                const rest = prev.filter((id) => !ofThis.has(id));
                                return Array.from(new Set([...rest, ...vals]));
                              });
                              scheduleDraftSave();
                            }}
                          >
                            <Row gutter={[8, 8]}>
                              {colorsOfFabric.map((c) => (
                                <Col xs={12} sm={8} md={6} lg={4} key={c.id}>
                                  <Checkbox value={c.id}>{c.color_name}</Checkbox>
                                </Col>
                              ))}
                            </Row>
                          </Checkbox.Group>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </div>
            )}

            {/* STEP 3 */}
            {uiStep === 3 && (
              <div className="animate-fade-in">
                <div className="mb-4">
                  <Space wrap style={{ gap: 8, marginBottom: 8 }}>
                    {sizes.map(s => (
                      <Tag key={s.id} closable onClose={() => handleRemoveVariant(s.id)} className="text-sm">
                        {s.size}
                      </Tag>
                    ))}
                    <Button type="dashed" onClick={handleAddVariant} icon={<PlusOutlined />}>
                      Add Variant
                    </Button>
                  </Space>

                  <Space wrap style={{ gap: 12 }}>
                    {isSuperAdmin && (
                      <>
                        <span className="font-medium text-gray-700">Base Cost Price(£):</span>
                        <InputNumber min={0} value={factoryBase} onChange={(v) => { setFactoryBase(v); scheduleDraftSave(); }} size="middle" style={{ width: 110 }} />
                      </>
                    )}
                    <span className="font-medium text-gray-700">Base Sale Price(€):</span>
                    <InputNumber min={0} value={priceBase} onChange={(v) => { setPriceBase(v); scheduleDraftSave(); }} size="middle" style={{ width: 110 }} />

                    <span className="font-medium text-gray-700">Gap per size(€):</span>
                    <InputNumber
                      min={0}
                      value={priceStep}
                      onChange={(v) => { setPriceStep(Number.isFinite(v) ? v : 0); scheduleDraftSave(); }}
                      size="middle"
                      style={{ width: 110 }}
                    />
                  </Space>
                </div>

                <div style={{ overflowX: "auto", marginTop: 12 }}>
                  <Table
                    dataSource={dataSource}
                    columns={columns}
                    pagination={false}
                    bordered
                    size={screens.xs ? "small" : "middle"}
                    scroll={screens.xs ? { x: 300 } : undefined}
                  />
                </div>

                <div style={{ marginTop: 18 }}>
                  <Divider orientation="left">Create duplicate listing</Divider>
                  <Form layout="vertical">
                    <Form.Item label="Create duplicate listing" name="duplicateCount">
                      <InputNumber
                        min={0}
                        max={20}
                        value={duplicateCount}
                        onChange={handleDuplicateCountChange}
                        style={{ width: 280 }}
                        placeholder="0"
                        parser={(v) => (isNaN(parseInt(v)) ? 0 : parseInt(v))}
                        formatter={(v) => (isNaN(v) ? '0' : `${v}`)}
                        onKeyPress={(e) => { if (!/[0-9]/.test(e.key)) e.preventDefault(); }}
                      />
                    </Form.Item>
                  </Form>
                </div>

                <div style={{ marginTop: 12 }}>
                  <Divider orientation="left">Inventory</Divider>
                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <div className="flex items-center" style={{ gap: 12 }}>
                        <span className="font-semibold text-gray-700">Add to Inventory</span>
                        <Switch checked={markInventoryPart} onChange={(v) => { setMarkInventoryPart(v); scheduleDraftSave(); }} />
                      </div>
                      <div style={{ marginTop: 6, color: '#6b7280', fontSize: 12 }}>
                        Marks the created bed(s) as inventory parts so they appear in the Inventory list.
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            )}

            {/* Sticky action bar (UI same, only added draft status + save button on last step) */}
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 mx-2 mt-8 backdrop-blur-sm bg-white/95 action-bar-sticky">
              <div className="flex justify-between items-center">
                <Button
                  onClick={prev}
                  disabled={uiStep === 0}
                  icon={<ArrowLeftOutlined />}
                  size="large"
                  className="rounded-lg h-12 px-6 border-gray-300 text-gray-700 hover:text-gray-900 hover:border-blue-400"
                >
                  Back
                </Button>

                {uiStep < steps.length - 1 ? (
                  <Button
                    type="primary"
                    onClick={next}
                    icon={<ArrowRightOutlined />}
                    size="large"
                    className="rounded-lg h-12 px-8 bg-gradient-to-r from-blue-500 to-indigo-600 border-0 shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    Next Step
                  </Button>
                ) : (
                  <div className="flex gap-4 items-center">
                    <div style={{ minWidth: 180 }}>
                      {draftSaving ? (
                        <Text type="secondary">Saving draft…</Text>
                      ) : draftSavedAt ? (
                        <Text type="secondary">Draft saved {new Date(draftSavedAt).toLocaleTimeString()}</Text>
                      ) : (
                        <Text type="secondary">No draft saved yet</Text>
                      )}
                    </div>

                    <Button
                      onClick={handleManualSaveDraft}
                      loading={draftSaving}
                      size="large"
                      className="rounded-lg h-12 px-6 border-gray-300 text-gray-700 hover:text-gray-900"
                    >
                      Save Draft
                    </Button>

                    <Button
                      type="primary"
                      onClick={handleSubmit}
                      loading={saving}
                      size="large"
                      className="rounded-lg h-12 px-8 bg-gradient-to-r from-green-500 to-emerald-600 border-0 shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      <CheckCircleOutlined className="mr-2" />
                      {duplicateCount > 0
                        ? `Publish + ${duplicateCount} cop${duplicateCount > 1 ? "ies" : "y"}`
                        : 'Publish'}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Continue Draft Modal */}
          <Modal
            open={showDraftChoiceModal}
            closable={false}
            maskClosable={false}
            footer={null}
            centered
          >
            <div style={{ textAlign: "center" }}>
              <Title level={4}>You have an unfinished bed draft</Title>
              <Text type="secondary">Would you like to continue editing your latest draft?</Text>

              <div style={{ marginTop: 24, display: "flex", justifyContent: "center", gap: 16 }}>
                <Button
                  type="primary"
                  onClick={async () => {
                    if (latestDraft?.id) {
                      await loadDraftIntoForm(latestDraft.id);
                      setAutoSaveEnabled(true);
                    } else {
                      setShowDraftChoiceModal(false);
                      setAutoSaveEnabled(true);
                    }
                  }}
                >
                  Continue Draft
                </Button>

                <Button
                  danger
                  onClick={() => {
                    setDraftId(null);
                    setLatestDraft(null);
                    setShowDraftChoiceModal(false);
                    setAutoSaveEnabled(true);
                    message.success("Starting new bed product");
                  }}
                >
                  Start New Product
                </Button>
              </div>
            </div>
          </Modal>

          {/* Progress Modal (UNCHANGED) */}
          <Modal
            title="Saving Bed Configuration — Please do not close"
            open={progressVisible}
            footer={null}
            closable={false}
            maskClosable={false}
            centered
            width={screens.xs ? "90%" : "60%"}
            bodyStyle={{ padding: screens.xs ? 16 : 24 }}
          >
            <div style={{ textAlign: "center", padding: screens.xs ? "20px 0" : "40px 0" }}>
              {!operationComplete ? (
                <>
                  <Spin size="large" />
                  <div style={{ marginTop: 16, fontWeight: "bold", fontSize: screens.xs ? 14 : 16 }}>
                    {currentStepText}
                  </div>
                </>
              ) : (
                <div style={{ fontWeight: "bold", color: "green", fontSize: screens.xs ? 14 : 16 }}>
                  Bed configuration saved successfully!
                </div>
              )}
            </div>
          </Modal>

          {/* Local styles (UNCHANGED) */}
          <style>{`
            .animate-fade-in { animation: fadeIn 0.4s ease-in-out; }
            @keyframes fadeIn { from { opacity:0; transform: translateY(10px);} to { opacity:1; transform: translateY(0);} }
            .custom-steps .ant-steps-item-title { font-weight: 600; }

            .action-bar-sticky {
              position: sticky;
              bottom: 20px;
              z-index: 10;
            }

            .thumb-wrapper { position: relative; width: 72px; height: 72px; }
            .thumb {
              width: 100%;
              height: 100%;
              border-radius: 10px;
              overflow: hidden;
              border: 2px solid #e5e7eb;
              background: #fff;
              cursor: pointer;
              display: inline-block;
              padding: 0;
            }
            .thumb.active { border-color: #6366f1; }
            .thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }

            .move-controls {
              position: absolute;
              top: 50%;
              left: 4px;
              right: 4px;
              display: flex;
              justify-content: space-between;
              transform: translateY(-50%);
              opacity: 0;
              pointer-events: none;
            }
            .thumb-wrapper:hover .move-controls { opacity: 1; pointer-events: auto; }

            .move-btn {
              width: 20px;
              height: 20px;
              background: white;
              border: 1px solid #d1d5db;
              border-radius: 4px;
              cursor: pointer;
              display: inline-flex; align-items: center; justify-content: center; font-size: 12px;
            }
            .move-btn.disabled { opacity: 0.3; cursor: not-allowed; }

            .position-badge {
              position: absolute;
              top: 2px;
              left: 2px;
              right: auto;
              background: rgba(0,0,0,0.7);
              color: #fff;
              font-size: 10px;
              padding: 2px 6px;
              border-radius: 8px;
              z-index: 5;
            }

            .remove-btn {
              position: absolute;
              top: -6px;
              right: -6px;
              width: 20px;
              height: 20px;
              background: #ef4444;
              color: white;
              border-radius: 50%;
              border: none;
              cursor: pointer;
              opacity: 0;
              transition: opacity 0.15s ease;
              z-index: 10;
              display: inline-flex; align-items: center; justify-content: center; font-size: 12px;
            }
            .thumb-wrapper:hover .remove-btn { opacity: 1; }
          `}</style>
        </Col>

        {/* RIGHT: Live Preview (UNCHANGED) */}
        <Col xs={24} lg={8}>
          <div className="hidden lg:block">
            <BedLivePreview
              title={title}
              description={description}
              images={images}
              types={types}
              sizes={sizes}
              priceMap={priceMap}
              isSuperAdmin={isSuperAdmin}
              costPriceMap={costPriceMap}
              fabrics={fabrics}
              selectedFabrics={selectedFabrics}
              selectedFabricColors={selectedFabricColors}
              fullFabricColors={fullFabricColors}
              pkgNonOttoman={pkgNonOttoman}
              pkgOttoman={pkgOttoman}
              pkgCloud={pkgCloud}
              sizeDimensions={sizeDimensions}
            />
          </div>

          <div className="lg:hidden">
            <BedLivePreview
              title={title}
              description={description}
              images={images}
              types={types}
              sizes={sizes}
              priceMap={priceMap}
              isSuperAdmin={isSuperAdmin}
              costPriceMap={costPriceMap}
              fabrics={fabrics}
              selectedFabrics={selectedFabrics}
              selectedFabricColors={selectedFabricColors}
              fullFabricColors={fullFabricColors}
              pkgNonOttoman={pkgNonOttoman}
              pkgOttoman={pkgOttoman}
              pkgCloud={pkgCloud}
              sizeDimensions={sizeDimensions}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default AddNewBed;